import React, { createContext, useContext, useState, useCallback } from 'react';
import type { DateRangeFilter, CustomDateRange } from '../types/dashboard.types';

interface DashboardFilterContextType {
  dateRange: DateRangeFilter;
  customRange: CustomDateRange | null;
  onFilterChange: (filter: DateRangeFilter, custom?: CustomDateRange | null) => void;
  filterOpen: boolean;
  toggleFilterOpen: () => void;
  filterLoading: boolean;
  setFilterLoading: (v: boolean) => void;
}

const DashboardFilterContext = createContext<DashboardFilterContextType | undefined>(undefined);

export const DashboardFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dateRange, setDateRange] = useState<DateRangeFilter>('last30days');
  const [customRange, setCustomRange] = useState<CustomDateRange | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);

  const onFilterChange = useCallback((filter: DateRangeFilter, custom?: CustomDateRange | null) => {
    setDateRange(filter);
    setCustomRange(custom ?? null);
  }, []);

  const toggleFilterOpen = useCallback(() => {
    setFilterOpen((prev) => !prev);
  }, []);

  return (
    <DashboardFilterContext.Provider
      value={{
        dateRange,
        customRange,
        onFilterChange,
        filterOpen,
        toggleFilterOpen,
        filterLoading,
        setFilterLoading,
      }}
    >
      {children}
    </DashboardFilterContext.Provider>
  );
};

export const useDashboardFilter = () => {
  const ctx = useContext(DashboardFilterContext);
  if (!ctx) throw new Error('useDashboardFilter must be used within DashboardFilterProvider');
  return ctx;
};
